import React, { useContext } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { useCurrentUser, isOnyxFC } from '../context/UserContext';
import { SignOutContext } from '../auth/AuthProvider';

interface NavItem {
  to: string;
  label: string;
}

const baseNav: NavItem[] = [
  { to: '/', label: 'Dashboard' },
  { to: '/requests', label: 'Requests' },
  { to: '/payees', label: 'Payees' },
  { to: '/allocations', label: 'Fund Pools' },
];

interface Props {
  children: React.ReactNode;
  pageTitle: string;
}

export default function Layout({ children, pageTitle }: Props) {
  const { user } = useCurrentUser();
  const { instance, accounts } = useMsal();
  const clearProvider = useContext(SignOutContext);
  const navigate = useNavigate();

  const navItems = isOnyxFC(user)
    ? [...baseNav, { to: '/admin', label: 'Admin' }]
    : baseNav;

  async function handleSignOut() {
    const account = accounts[0];
    if (account) {
      await instance.logoutRedirect({ account, onRedirectNavigate: () => false });
    }
    clearProvider();
    navigate('/');
  }

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Left nav */}
      <aside className="w-56 bg-slate-900 text-white flex flex-col flex-shrink-0">
        {/* Brand */}
        <div className="px-5 pt-6 pb-4 border-b border-slate-700">
          <p className="text-sm font-bold text-white leading-tight">Onyx Financial Coordinator</p>
          <p className="text-xs text-slate-400 mt-0.5">BHSD – Special Projects</p>
        </div>

        {/* Nav links */}
        <nav className="flex-1 px-3 py-4 space-y-0.5">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) =>
                `block px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-slate-700 text-white'
                    : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        {/* New Request button */}
        <div className="px-3 pb-3">
          <NavLink
            to="/requests/new"
            className="block w-full text-center px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
          >
            + New Request
          </NavLink>
        </div>

        {/* Sign Out */}
        <div className="px-3 pb-5 border-t border-slate-700 pt-3">
          <button
            onClick={handleSignOut}
            className="w-full text-left px-3 py-2 text-sm text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
          >
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main content area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top bar */}
        <header className="bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between flex-shrink-0">
          <h1 className="text-lg font-semibold text-gray-900">{pageTitle}</h1>
          {user && (
            <span className="text-sm text-gray-500">
              {user.display_name}
              <span className="mx-1 text-gray-300">·</span>
              <span className="text-gray-400">{user.roles[0] ?? ''}</span>
            </span>
          )}
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-6">
          {children}
        </main>
      </div>
    </div>
  );
}
